import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { documentAPI } from '../services/api';
import SearchableMultiSelect from './SearchableMultiSelect';
import Swal from 'sweetalert2';
import './DocumentList.css';

const DocumentList = () => {
  const { user, isAuthenticated } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // States cho upload tài liệu
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    file: null,
    recipientUnits: []
  });
  const [availableUnits, setAvailableUnits] = useState([]);

  useEffect(() => {
    fetchDocuments();
    fetchUnits();
  }, [pagination?.currentPage]);

  const fetchUnits = async () => {
    try {
      const response = await documentAPI.getUnitsForDocument();
      setAvailableUnits(response.data);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  // Debounce search để tránh gọi API quá nhiều
  useEffect(() => {
    if (searchTerm === '') {
      // Nếu search rỗng, gọi ngay lập tức
      setIsSearching(false);
      fetchDocuments();
    } else {
      // Nếu có search term, debounce 500ms
      setIsSearching(true);
      const timeoutId = setTimeout(() => {
        setIsSearching(false);
        fetchDocuments();
      }, 500);

      return () => {
        clearTimeout(timeoutId);
        setIsSearching(false);
      };
    }
  }, [searchTerm]);

  const fetchDocuments = async () => {
    try {
      // Chỉ hiển thị loading nếu không đang trong quá trình search debounce
      if (!isSearching) {
        setLoading(true);
      }

      const response = await documentAPI.getPublicDocuments({
        search: searchTerm,
        page: pagination?.currentPage || 1,
        limit: pagination?.itemsPerPage || 10
      });

      setDocuments(response.data.documents);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Fetch documents error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!uploadForm.title.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Thiếu thông tin',
        text: 'Vui lòng nhập tên tài liệu',
        confirmButtonColor: '#1e3a8a'
      });
      return;
    }

    if (!uploadForm.file) {
      Swal.fire({
        icon: 'warning',
        title: 'Thiếu thông tin',
        text: 'Vui lòng chọn file PDF hoặc Word',
        confirmButtonColor: '#1e3a8a'
      });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('title', uploadForm.title.trim());
      formData.append('document', uploadForm.file);

      // Thêm danh sách đơn vị nhận (nếu có)
      if (uploadForm.recipientUnits.length > 0) {
        formData.append('recipientUnitIds', JSON.stringify(uploadForm.recipientUnits));
      }

      await documentAPI.uploadDocument(formData);

      setUploadForm({ title: '', file: null, recipientUnits: [] });
      setShowUploadForm(false);
      fetchDocuments(); // Refresh danh sách
      
      Swal.fire({
        icon: 'success',
        title: 'Thành công!',
        text: 'Upload tài liệu thành công',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Upload error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: error.response?.data?.message || 'Lỗi khi upload tài liệu',
        confirmButtonColor: '#1e3a8a'
      });
    } finally {
      setUploading(false);
    }
  };





  const handleDownload = async (qrCode, originalName) => {
    try {
      const response = await documentAPI.downloadDocumentByQR(qrCode);

      // Tạo blob URL và trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: 'Lỗi khi tải xuống tài liệu',
        confirmButtonColor: '#1e3a8a'
      });
    }
  };

  const handleView = async (qrCode, originalName) => {
    // Kiểm tra loại file dựa trên extension
    const fileExtension = originalName.toLowerCase().split('.').pop();
    const apiUrl = process.env.REACT_APP_API_URL || 'https://apiqldangkyxe.nguyenluan.vn';

    if (fileExtension === 'pdf') {
      // PDF: Mở trực tiếp trong tab mới
      const pdfUrl = `${apiUrl}/api/documents/public/${qrCode}/download?view=true`;
      window.open(pdfUrl, '_blank');
    } else if (fileExtension === 'doc' || fileExtension === 'docx') {
      // Word: Thông báo và download
      const result = await Swal.fire({
        icon: 'info',
        title: 'File Word',
        text: 'File Word không thể xem trực tiếp trong trình duyệt. Bạn có muốn tải xuống để xem không?',
        showCancelButton: true,
        confirmButtonColor: '#1e3a8a',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Tải xuống',
        cancelButtonText: 'Hủy'
      });
      
      if (result.isConfirmed) {
        const downloadUrl = `${apiUrl}/api/documents/public/${qrCode}/download`;
        window.open(downloadUrl, '_blank');
      }
    } else {
      // Các loại file khác: download
      const downloadUrl = `${apiUrl}/api/documents/public/${qrCode}/download`;
      window.open(downloadUrl, '_blank');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="document-list">
      <div className="document-list-header">
        <div className="header-content">
          <h1>Danh sách tài liệu</h1>
          <p>Tìm kiếm và tải xuống tài liệu</p>
        </div>
        {/* Hiển thị nút thêm tài liệu cho user có quyền */}
        {isAuthenticated && user && (user.role === 'admin' || user.role === 'document' || user.role === 'unit' || user.role === 'supervisor') && (
          <button
            className="btn-primary"
            onClick={() => setShowUploadForm(true)}
          >
            Thêm tài liệu
          </button>
        )}
      </div>

      {/* Form tìm kiếm */}
      <div className="search-section">
        <div className="search-group">
          <input
            type="text"
            placeholder="Tìm kiếm tài liệu..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
          {isSearching && (
            <div className="search-indicator">
              <span>Đang tìm kiếm...</span>
            </div>
          )}
        </div>
      </div>

      {/* Danh sách tài liệu */}
      {loading ? (
        <div className="loading">Đang tải...</div>
      ) : (
        <div className="documents-container">
          {documents.length === 0 ? (
            <div className="no-documents">
              <p>Không tìm thấy tài liệu nào</p>
            </div>
          ) : (
            <div className="documents-table">
              <table>
                <thead>
                  <tr>
                    <th>Tên tài liệu</th>
                    <th>Ngày upload</th>
                    <th>Lượt tải</th>
                    <th>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map(doc => (
                    <tr key={doc.id}>
                      <td className="document-title-cell">{doc.title}</td>
                      <td>{formatDate(doc.uploadDate)}</td>
                      <td>{doc.downloadCount}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-view"
                            onClick={() => handleView(doc.qrCode, doc.originalName)}
                          >
                            Xem
                          </button>
                          <button
                            className="btn-download"
                            onClick={() => handleDownload(doc.qrCode, doc.originalName)}
                          >
                            Tải xuống
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Phân trang */}
          {pagination && pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                disabled={pagination.currentPage === 1}
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
              >
                Trước
              </button>
              <span>
                Trang {pagination.currentPage} / {pagination.totalPages}
              </span>
              <button
                disabled={pagination.currentPage === pagination.totalPages}
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
              >
                Sau
              </button>
            </div>
          )}
        </div>
      )}

      {/* Form upload tài liệu */}
      {showUploadForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Upload tài liệu</h3>
              <button
                className="modal-close"
                onClick={() => setShowUploadForm(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUpload} className="upload-form">
              <div className="form-group">
                <label>Tên tài liệu:</label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Nhập tên tài liệu..."
                  required
                />
              </div>
              <div className="form-group">
                <label>Chọn file PDF hoặc Word:</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setUploadForm(prev => ({ ...prev, file: e.target.files[0] }))}
                  required
                />
                <small className="file-hint">Hỗ trợ: PDF, DOC, DOCX (tối đa 50MB)</small>
              </div>

              <div className="form-group">
                <label>Gửi cho đơn vị (không bắt buộc):</label>
                <SearchableMultiSelect
                  options={availableUnits}
                  selectedValues={uploadForm.recipientUnits}
                  onChange={(selectedValues) => setUploadForm(prev => ({ ...prev, recipientUnits: selectedValues }))}
                  placeholder="Chọn đơn vị nhận..."
                  searchPlaceholder="Gõ tên đơn vị để tìm kiếm..."
                />
                <small className="units-hint">
                  Không chọn = {user?.role === 'admin' ? 'Gửi cho tất cả đơn vị' : 'Chỉ admin thấy'}
                </small>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowUploadForm(false)}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={uploading}
                >
                  {uploading ? 'Đang upload...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentList;
